import { useState } from 'react'

import { useTranslation } from 'react-i18next'

import { ExternalLinkIcon, InfoIcon, XIcon } from 'lucide-react'

import { isInAppBrowser } from '@/lib/utils'

import toast from './toast'
import { SmartButton } from './ui-custom/SmartButton'

/**
 * Banner that shows when the app is opened in an in-app browser (Instagram, Facebook, etc.)
 * Recommends opening in a real browser for better compatibility
 */
export function InAppBrowserBanner() {
  const { t } = useTranslation(['common'])
  const [dismissed, setDismissed] = useState(false)

  if (!isInAppBrowser() || dismissed) return null

  const currentUrl = window.location.href

  const handleCopyLink = () => {
    navigator.clipboard.writeText(currentUrl)
    toast.success(t('common:inAppBrowser.linkCopied', 'Link copied to clipboard!'))
  }

  const handleOpenInBrowser = () => {
    // For iOS: redirect to Safari
    if (navigator.userAgent.includes('iPhone') || navigator.userAgent.includes('iPad')) {
      window.location.href = currentUrl
      return
    }

    // For Android: try opening in external browser
    if (navigator.userAgent.includes('Android')) {
      const opened = window.open(currentUrl, '_blank')
      if (opened) {
        // Successfully opened in external browser
        return
      }
    }

    // Fallback: copy link to clipboard
    handleCopyLink()
  }

  return (
    <div className='sticky top-0 z-[999] border-b border-amber-200 bg-amber-50 dark:border-amber-200/30 dark:bg-amber-950/20'>
      <div className='mx-auto max-w-7xl px-3 py-3 sm:px-6'>
        <div className='flex items-center gap-2 sm:gap-3'>
          <InfoIcon className='h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400' />

          <p className='flex-1 text-xs leading-tight text-amber-900 sm:text-sm dark:text-amber-100'>
            {t('common:inAppBrowser.description', 'For the best experience, please open in Safari or Chrome.')}
          </p>

          <div className='flex shrink-0 items-center gap-1'>
            {/* Show only primary action button on mobile */}
            <SmartButton
              size='sm'
              onClick={handleOpenInBrowser}
              icon={<ExternalLinkIcon className='h-3 w-3' />}
              className='h-7 bg-amber-600 px-2 text-[10px] text-white hover:bg-amber-700 sm:text-xs dark:bg-amber-500 dark:hover:bg-amber-600'
            >
              <span className='hidden sm:inline'>{t('common:inAppBrowser.openInBrowser', 'Open')}</span>
              <span className='sm:hidden'>{t('common:inAppBrowser.open', 'Open')}</span>
            </SmartButton>

            {/* Close button */}
            <button
              onClick={() => setDismissed(true)}
              className='rounded p-1 text-amber-600 transition-colors hover:bg-amber-100 hover:text-amber-900 dark:text-amber-400 dark:hover:bg-amber-900/30 dark:hover:text-amber-200'
              aria-label={t('common:actions.dismiss', 'Dismiss')}
            >
              <XIcon className='h-3.5 w-3.5' />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
